import React from 'react';
import { Box, Button, FormControl, Grid, InputLabel, MenuItem, Paper, Select, SelectChangeEvent, TextField } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { MultiStatusSelect, SearchActionButtons, SearchResultCounter, SearchTextField, StatusSelect } from '../common';
import { formatDateToISO } from '../../utils/dateUtils';
import {
  BLOCKED_STATUS_DISPLAY,
  RESIGNED_STATUS_DISPLAY,
  ROLE_TYPE_OPTIONS,
  TALK_STATUS_DISPLAY,
  TEST_ACCOUNT_STATUS_DISPLAY
} from '../../types/patient';
import { DEVELOPER_THEME } from '../../types/common';
import { useAuth } from '../../hooks/useAuth';

interface PatientSearchFormProps {
  // 検索フィールド
  searchTerm: string;
  onSearchTermChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClearSearchField: () => void;
  emailSearchTerm: string;
  onEmailSearchTermChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClearEmailField: () => void;
  userIdSearchTerm?: string;
  onUserIdSearchTermChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClearUserIdField?: () => void;
  selectedRoles: string[];
  onRoleChange: (event: any) => void;
  talkStatus: string;
  onTalkStatusChange: (event: SelectChangeEvent) => void;
  blockedStatus: string;
  onBlockedStatusChange: (event: SelectChangeEvent) => void;
  resignedStatus: string;
  onResignedStatusChange: (event: SelectChangeEvent) => void;
  conversationCount: string;
  onConversationCountChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  conversationCountOperator: string;
  onConversationCountOperatorChange: (event: SelectChangeEvent) => void;
  testAccountStatus: string;
  onTestAccountStatusChange: (event: SelectChangeEvent) => void;
  // 登録日フィルター
  createdAtStart: string | null;
  onCreatedAtStartChange: (date: string | null) => void;
  createdAtEnd: string | null;
  onCreatedAtEndChange: (date: string | null) => void;
  // 操作
  totalCount: number;
  onSearch: (event: React.FormEvent) => void;
  onClear: () => void;
  onCSVExport?: () => void;
}

const PatientSearchForm: React.FC<PatientSearchFormProps> = ({
  searchTerm,
  onSearchTermChange,
  onClearSearchField,
  emailSearchTerm,
  onEmailSearchTermChange,
  onClearEmailField,
  userIdSearchTerm = '',
  onUserIdSearchTermChange,
  onClearUserIdField,
  selectedRoles,
  onRoleChange,
  talkStatus,
  onTalkStatusChange,
  blockedStatus,
  onBlockedStatusChange,
  resignedStatus,
  onResignedStatusChange,
  conversationCount,
  onConversationCountChange,
  conversationCountOperator,
  onConversationCountOperatorChange,
  testAccountStatus,
  onTestAccountStatusChange,
  createdAtStart,
  onCreatedAtStartChange,
  createdAtEnd,
  onCreatedAtEndChange,
  totalCount,
  onSearch,
  onClear,
  onCSVExport
}) => {
  const { isDeveloper } = useAuth();
  return (
    <Paper
      component="form"
      elevation={2}
      sx={{ p: 3, mb: 3 }}
      onSubmit={onSearch}
    >
      {/* 1列目: ロール、名前・ニックネーム、メールアドレス、退会状態 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <MultiStatusSelect
            label="ロール"
            id="role-filter"
            value={selectedRoles}
            onChange={onRoleChange}
            options={ROLE_TYPE_OPTIONS}
            emptyLabel="すべて"
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <SearchTextField
            label="名前 or ニックネーム"
            value={searchTerm}
            onChange={onSearchTermChange}
            onClear={onClearSearchField}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <SearchTextField
            label="メールアドレス"
            value={emailSearchTerm}
            onChange={onEmailSearchTermChange}
            onClear={onClearEmailField}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <StatusSelect
            label="退会状態"
            id="resigned-status-filter"
            value={resignedStatus}
            onChange={onResignedStatusChange}
            options={RESIGNED_STATUS_DISPLAY}
          />
        </Grid>
      </Grid>

      {/* 2列目: 対話状態、対話回数、テストアカウント、対話拒否 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <StatusSelect
            label="対話状態"
            id="talk-status-filter"
            value={talkStatus}
            onChange={onTalkStatusChange}
            options={TALK_STATUS_DISPLAY}
          />
        </Grid>

        {/* 対話回数 */}
        <Grid item xs={12} md={3}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
            {/* 回数入力 */}
            <TextField
              size="small"
              label="対話回数"
              placeholder="例: 5"
              variant="outlined"
              value={conversationCount}
              onChange={onConversationCountChange}
              type="text"
              inputProps={{
                pattern: '[0-9]*',
                inputMode: 'numeric',
                style: { textAlign: 'center' }
              }}
              sx={{ width: '50%', mr: 1 }}
            />

            {/* 演算子選択 - 回数が入力されたら文字濃く */}
            <FormControl size="small" sx={{ width: '50%' }}>
              <InputLabel
                id="conversation-count-operator-label"
                sx={{
                  color: conversationCount ? 'rgba(0, 0, 0, 0.87)' : 'rgba(0, 0, 0, 0.38)'
                }}
              >
                回数条件
              </InputLabel>
              <Select
                labelId="conversation-count-operator-label"
                id="conversation-count-operator"
                value={conversationCountOperator}
                onChange={onConversationCountOperatorChange}
                label="回数条件"
                fullWidth
                sx={{
                  '& .MuiSelect-select': {
                    color: conversationCount ? 'rgba(0, 0, 0, 0.87)' : 'rgba(0, 0, 0, 0.38)'
                  }
                }}
              >
                <MenuItem value="eq">と等しい</MenuItem>
                <MenuItem value="gte">以上</MenuItem>
                <MenuItem value="lte">以下</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Grid>

        <Grid item xs={12} md={3}>
          <StatusSelect
            label="テストアカウント"
            id="test-account-filter"
            value={testAccountStatus}
            onChange={onTestAccountStatusChange}
            options={TEST_ACCOUNT_STATUS_DISPLAY}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <StatusSelect
            label="対話拒否状態"
            id="blocked-status-filter"
            value={blockedStatus}
            onChange={onBlockedStatusChange}
            options={BLOCKED_STATUS_DISPLAY}
          />
        </Grid>
      </Grid>

      {/* 3列目: 登録日範囲、User ID（開発者モードのみ） */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
            {/* 開始日入力 */}
            <TextField
              label="登録日(開始)"
              type="date"
              size="small"
              value={createdAtStart || ''}
              onChange={(e) => {
                const newStartDate = e.target.value || null;
                onCreatedAtStartChange(newStartDate);
                // 開始日が変更されたとき、終了日のチェック
                // もし終了日が設定済みで、新しい開始日より前の日付なら終了日をクリア
                if (newStartDate && createdAtEnd && createdAtEnd < newStartDate) {
                  onCreatedAtEndChange(null);
                }
              }}
              InputLabelProps={{
                shrink: true,
              }}
              // 本日までの日付しか選べないようにする
              inputProps={{
                max: formatDateToISO(new Date()) // 本日の日付
              }}
              sx={{ width: '50%', mr: 1 }}
            />

            {/* 終了日入力 - 開始日がある場合のみ有効 */}
            <TextField
              label="登録日(終了)"
              type="date"
              size="small"
              value={createdAtEnd || ''}
              onChange={(e) => onCreatedAtEndChange(e.target.value || null)}
              InputLabelProps={{
                shrink: true,
              }}
              // 開始日が入力されている場合のみ有効化（登録日(終了)のみの検索はサポートしない）
              disabled={!createdAtStart}
              // 開始日以降、本日までの日付しか選べないようにする
              inputProps={{
                min: createdAtStart || undefined,
                max: formatDateToISO(new Date()) // 本日の日付
              }}
              sx={{ width: '50%' }}
            />
          </Box>
        </Grid>

        {/* 開発者モードの場合はUser ID検索フィールドを追加 */}
        {isDeveloper() && (
          <Grid item xs={12} md={3}>
            <SearchTextField
              label="User ID"
              value={userIdSearchTerm}
              onChange={onUserIdSearchTermChange || (() => {})}
              onClear={onClearUserIdField || (() => {})}
              placeholder="部分一致で検索 (開発者モード)"
              sx={{
                bgcolor: `${DEVELOPER_THEME.BACKGROUND_ALPHA}`,
                borderRadius: 1,
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: DEVELOPER_THEME.DARK,
                  },
                  '&:hover fieldset': {
                    borderColor: DEVELOPER_THEME.BORDER,
                  },
                  '& fieldset': {
                    borderColor: `${DEVELOPER_THEME.BORDER_LIGHT_ALPHA}`,
                    borderStyle: 'dashed',
                  }
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(0, 0, 0, 0.6)',
                  '&.Mui-focused': {
                    color: DEVELOPER_THEME.DARK,
                  }
                },
                '& .MuiInputBase-input': {
                  '&::placeholder': {
                    color: 'rgba(121, 85, 72, 0.5)',
                    fontStyle: 'italic'
                  }
                },
                '& .MuiIconButton-root': {
                  color: DEVELOPER_THEME.DARK,
                }
              }}
            />
          </Grid>
        )}
      </Grid>

      {/* 3列目: 件数表示、検索ボタン、クリアボタン - シャドウなし */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2, position: 'relative' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SearchResultCounter count={totalCount}/>
          {onCSVExport && (
            <Button
              type="button"
              variant="outlined"
              color="info"
              size="small"
              startIcon={<DownloadIcon/>}
              onClick={onCSVExport}
            >
              ダウンロード
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, position: 'absolute', right: 0 }}>
          <SearchActionButtons
            onClear={onClear}
            searchLabel="検索"
            clearLabel="クリア"
          />
        </Box>
      </Box>
    </Paper>
  );
};
export default PatientSearchForm;
